import React, { useEffect, useState, useMemo } from "react";
import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import {
  ShowChart as ShowChartIcon,
  Grade as GradeIcon,
  Bookmarks as KpiIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import "./DashboardPage.scss";

// --- API & Models ---
import APIService from "../../Api/ApiService";
import {
  ServiceStatus,
  TrendData,
  MonitorGroup,
  FavoriteService,
  ServiceStatusAggregation,
} from "../../Models/Types";
import { jitterTrendData } from "../../JitterUtil";
import { BackToAppDeepLink } from "../../Routes";
import MonitorList from "../../Components/MonitorList/MonitorList";
import GroupsSection from "../../Components/GroupsSection/GroupsSection";
import OverviewChart from "../../Components/OverviewChart/OverviewChart";
import DashboardHeader from "../../Components/DashboardHeader/DashboardHeader";
import GroupModal from "../../Components/GroupModal/GroupModal";

export const MAX_SHOW_ITEMS = 50;

export const matchesAppSearch = (
  agg: ServiceStatusAggregation,
  search: string,
): boolean => {
  const s = search.toLowerCase();
  return (
    agg.application.toLowerCase().includes(s) ||
    agg.items.some((item) =>
      (item.responsible || "").toLowerCase().includes(s),
    )
  );
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

  // --- Data State ---
  const [statusData, setStatusData] = useState<ServiceStatus[]>([]);
  // We keep the raw data, but the main driver is now the Aggregated data
  const [aggregatedData, setAggregatedData] = useState<
    ServiceStatusAggregation[]
  >([]);

  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // --- UI Loading State ---
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // --- Filter & View State ---
  const [searchText, setSearchText] = useState("");
  const [sortOrder, setSortOrder] = useState<"last_exec" | "alpha">(
    "last_exec",
  );
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0);
  const [filterByRunState, setFilterByRunState] = useState<
    "any" | "running" | "stopped" | "paused"
  >("any");

  // --- Favorites & Groups Data ---
  const [favorites, setFavorites] = useState<FavoriteService[]>([]);
  const [groups, setGroups] = useState<MonitorGroup[]>([]);

  // --- Modal & Group Selection State ---
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<MonitorGroup | null>(null);
  const [filteringGroup, setFilteringGroup] = useState<MonitorGroup | null>(
    null,
  );

  // --- Aggregation Logic ---
  const aggregateServicesByApplication = (
    services: ServiceStatus[],
  ): ServiceStatusAggregation[] => {
    const groups = new Map<number, ServiceStatusAggregation>();

    for (const service of services) {
      const appId = service.application_id;

      if (!groups.has(appId)) {
        groups.set(appId, {
          application_id: service.application_id,
          application: service.application,
          application_description: service.application_description,
          items: [],
          maxDuration: -Infinity, // Tracks max of KO monitors
          minDuration: Infinity, // Tracks min of OK monitors
          okMonitors: 0,
          koMonitors: 0,
          last_execution_time: "",
          aggregate_status: true,
          is_kpi: false,
        });
      }

      const group = groups.get(appId)!;
      group.items.push(service);

      const duration = service.duration_secs || 0;

      // --- Conditional Metrics Logic ---
      if (service.run_status) {
        // 1. OK Monitor logic
        group.okMonitors++;

        // Update minDuration ONLY for successful monitors
        if (duration < group.minDuration) {
          group.minDuration = duration;
        }
      } else {
        // 2. KO Monitor logic
        group.koMonitors++;
        group.aggregate_status = false;

        // Update maxDuration ONLY for failed monitors
        if (duration > group.maxDuration) {
          group.maxDuration = duration;
        }
      }

      // --- KPI & Execution Time ---
      if (service.kpi) {
        group.is_kpi = true;
      }

      if (
        !group.last_execution_time ||
        new Date(service.run_execution_time) >
          new Date(group.last_execution_time)
      ) {
        group.last_execution_time = service.run_execution_time;
      }
    }

    return Array.from(groups.values()).map((group) => {
      // Reset defaults if no matching services were found for that specific metric
      if (group.maxDuration === -Infinity) group.maxDuration = 0;
      if (group.minDuration === Infinity) group.minDuration = 0;
      return group;
    });
  };

  // --- API Calls ---

  const fetchStatusData = async () => {
    try {
      setLoadingStatus(true);
      const status = await APIService.GetMonitoredServicesStatus();
      setStatusData(status);
      setAggregatedData(aggregateServicesByApplication(status));
    } catch (err) {
      console.error("Errore fetch status data", err);
      setStatusData([]);
      setAggregatedData([]);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchTrendData = async () => {
    try {
      setLoadingTrend(true);
      const trend = await APIService.GetMonitoredServicesTrend(24);
      setTrendData(trend);
    } catch (err) {
      console.error("Errore fetch trend data", err);
      setTrendData(null);
    } finally {
      setLoadingTrend(false);
    }
  };

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const groupsData = await APIService.GetGroups();
      setGroups(groupsData);
    } catch (err) {
      console.error("Errore fetch groups", err);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const favs = await APIService.GetAllFavorited();
      setFavorites(favs);
    } catch (err) {
      console.error("Errore fetch favorites", err);
      setFavorites([]);
    }
  };

  useEffect(() => {
    // Initial fetch on component mount
    fetchStatusData();
    fetchTrendData();
    fetchGroups();
    fetchFavorites();

    // Set up an interval to fetch trend data every 5 minutes (300,000 milliseconds)
    const trendIntervalId = setInterval(
      () => {
        console.log("Fetching trend data on 5-minute interval...");
        fetchTrendData();
      },
      5 * 60 * 1000,
    );

    // Cleanup function: clears the interval when the component is unmounted
    return () => {
      console.log("Component unmounted: clearing trend data interval...");
      clearInterval(trendIntervalId);
    };
  }, []);

  // --- Handlers ---

  const handleLogout = () => {
    localStorage.clear();
    navigate(BackToAppDeepLink);
  };

  // Bulk Favorite Toggle
  const toggleApplicationFavorite = async (
    appId: number,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    const ownerName = localStorage.getItem("loggedUserId") || "";

    try {
      const response = await APIService.SetIsFavorited(
        appId,
        favorites.some((fav) => fav.applicationId === appId) ? false : true,
      );

      setFavorites(response.current_favorites);
    } catch (err) {
      console.error("Error bulk toggling favorites", err);
    }
  };

  // --- Group Handlers ---

  const handleOpenGroupModal = (group?: MonitorGroup) => {
    setEditingGroup(group || null);
    setIsGroupModalOpen(true);
  };

  const handleSaveGroup = async (
    groupName: string,
    selectedIds: number[],
    threshold: number,
  ) => {
    setIsGroupModalOpen(false);
    const servicesStr = selectedIds.join(",");

    try {
      if (editingGroup) {
        const updatedGroup = await APIService.UpdateGroup(
          editingGroup.id,
          groupName,
          servicesStr,
          threshold,
        );
        setGroups((prev) =>
          prev.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)),
        );
      } else {
        const newGroup = await APIService.CreateGroup(
          groupName,
          servicesStr,
          threshold,
        );
        setGroups((prev) => [...prev, newGroup]);
      }
    } catch (err) {
      console.error("Errore salvataggio gruppo:", err);
    }
  };

  const handleDeleteGroup = async () => {
    if (!editingGroup) return;
    try {
      await APIService.DeleteGroup(editingGroup.id);
      setGroups((prev) => prev.filter((g) => g.id !== editingGroup!.id));
      setIsGroupModalOpen(false);
    } catch (err) {
      console.error("Errore eliminazione gruppo:", err);
    }
  };

  const backToMain = () => {
    setActiveTab(0);
    setFilteringGroup(null);
    window.scrollTo(0, 0);
  };

  const filterByGroupIfAny = (aggregations: ServiceStatusAggregation[]) => {
    if (filteringGroup) {
      const idsInGroup = filteringGroup.applicationIdsInGroup
        .split(",")
        .map(Number);
      // Show aggregation if it contains items in the group
      return aggregations.filter((agg) =>
        idsInGroup.includes(agg.application_id),
      );
    }
    return aggregations;
  };

  const filterBeforeStatus = () => {
    let result = [...aggregatedData];

    // 1. Tab Filter
    if (activeTab === 1) {
      result = result.filter((agg) =>
        favorites.some((fav) => fav.applicationId === agg.application_id),
      );
    } else if (activeTab === 2) {
      // KPI: Show App if it contains KPI services
      //result = result.filter((agg) => agg.is_kpi);
    }

    return filterByGroupIfAny(result);
  };

  // --- Filtering Logic for AGGREGATED List ---
  const filteredAggregations = useMemo(() => {
    let result = filterBeforeStatus();

    // 2. Search Filter (Search in App Name OR any Item Name)
    if (searchText && activeTab == 0) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter((agg) => matchesAppSearch(agg, lowerSearch));
    }

    // 3. Sort Order
    result.sort((a, b) => {
      if (sortOrder === "alpha") {
        return a.application.trim().localeCompare(b.application.trim());
      } else {
        // Sort: KO first (descending maxDuration), then OK (ascending minDuration)
        if (a.aggregate_status === b.aggregate_status) {
          if (!a.aggregate_status) {
            // Both KO: descending duration
            return b.maxDuration - a.maxDuration;
          } else {
            // Both OK: ascending duration
            return a.minDuration - b.minDuration;
          }
        }
        // KO (false) comes before OK (true)
        return a.aggregate_status ? 1 : -1;
      }
    });

    // 4. Run State Filter
    if (filterByRunState === "running") {
      result = result.filter((agg) => {
        return agg.aggregate_status;
      });
    }
    if (filterByRunState === "stopped") {
      result = result.filter((agg) => {
        return !agg.aggregate_status;
      });
    }

    return result;
  }, [
    aggregatedData,
    activeTab,
    searchText,
    sortOrder,
    favorites,
    filteringGroup,
    filterByRunState,
  ]);

  return (
    <div className="dashboard-page-root">
      <DashboardHeader lastUpdated={lastUpdated} onLogout={handleLogout} />

      {activeTab === 0 && !filteringGroup && (
        <OverviewChart trendData={trendData} loading={loadingTrend} />
      )}

      {!loadingStatus && activeTab === 0 && !filteringGroup && (
        <GroupsSection
          groups={groups}
          loading={loadingGroups}
          onOpenModal={handleOpenGroupModal}
          onSelectGroup={setFilteringGroup}
          allAggregations={aggregatedData} // Pass aggregation data for counting
        />
      )}

      <MonitorList
        aggregations={
          activeTab > 0
            ? filteredAggregations
            : filteredAggregations.slice(0, MAX_SHOW_ITEMS)
        }
        allResultsBeforeStatusFilter={filterBeforeStatus()}
        favorites={favorites}
        loading={loadingStatus}
        activeTab={activeTab}
        filteringGroup={filteringGroup}
        searchText={searchText}
        setSearchText={setSearchText}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        filterByRunState={filterByRunState}
        setFilterByRunState={setFilterByRunState}
        onToggleFavorite={toggleApplicationFavorite} // Handler takes appId
        onBackToMain={backToMain}
      />

      <BottomNavigation
        value={activeTab}
        onChange={(event, newValue) => {
          setActiveTab(newValue);
          setFilteringGroup(null);
          setSearchText("");
          window.scrollTo(0, 0);
        }}
        showLabels
        className="bottom-nav-fixed"
      >
        <BottomNavigationAction
          style={{ opacity: loadingStatus ? "0.2" : "1" }}
          disabled={loadingStatus}
          label="Monitoraggio"
          icon={<ShowChartIcon />}
        />
        <BottomNavigationAction
          style={{ opacity: loadingStatus ? "0.2" : "1" }}
          disabled={loadingStatus}
          label="Preferiti"
          icon={<GradeIcon />}
        />
        <BottomNavigationAction
          style={{ opacity: loadingStatus ? "0.2" : "1" }}
          disabled={loadingStatus}
          label="KSP"
          icon={<KpiIcon />}
        />
      </BottomNavigation>

      <GroupModal
        open={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        editingGroup={editingGroup}
        aggregatedData={aggregatedData} // Pass aggregations to Modal
      />
    </div>
  );
};

export default DashboardPage;
